import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import logisticsApi from '@utils/logisticsApi';
import { useI18n } from '@/i18n';
import PageHeader from '@components/PageHeader';

const emptyLine = { productId: '', quantity: '' };

export default function NewRequest() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [justification, setJustification] = useState('');
  const [urgency, setUrgency] = useState('normal');
  const [lines, setLines] = useState([{ ...emptyLine }]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    logisticsApi.get('/catalog/products').then(({ data }) => setProducts(data.products || [])).catch(() => {});
  }, []);

  function updateLine(index, field, value) {
    setLines((prev) => prev.map((l, i) => (i === index ? { ...l, [field]: value } : l)));
  }

  async function submit(e) {
    e.preventDefault();
    const validLines = lines
      .filter((l) => l.productId && l.quantity)
      .map((l) => ({ productId: l.productId, quantity: Number(l.quantity) }));
    if (!validLines.length) { setError(t('logisticsRequests.errFill')); return; }

    setBusy(true);
    setError('');
    try {
      await logisticsApi.post('/me/requests', { justification, urgency, lines: validLines });
      navigate('/requests');
    } catch (err) {
      setError(err.response?.data?.message || t('logisticsRequests.errSend'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader title={t('logisticsRequests.newTitle')} subtitle={t('logisticsRequests.subtitle')} />
      <div className="px-5 md:px-0 pb-6">
        <div className="card p-5">
          {error && <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}
          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="label">{t('logisticsRequests.urgency')}</label>
              <select className="input" value={urgency} onChange={(e) => setUrgency(e.target.value)}>
                <option value="low">{t('logisticsRequests.urgencyLow')}</option>
                <option value="normal">{t('logisticsRequests.urgencyNormal')}</option>
                <option value="high">{t('logisticsRequests.urgencyHigh')}</option>
              </select>
            </div>

            <div>
              <label className="label">{t('logisticsRequests.justification')}</label>
              <textarea className="input resize-none" rows={3} value={justification}
                placeholder={t('logisticsRequests.justificationPlaceholder')}
                onChange={(e) => setJustification(e.target.value)} />
            </div>

            <div className="space-y-2">
              {lines.map((line, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <select required value={line.productId} onChange={(e) => updateLine(i, 'productId', e.target.value)}
                    className="input flex-1">
                    <option value="">{t('logisticsRequests.selectProduct')}</option>
                    {products.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.sku})</option>)}
                  </select>
                  <input required type="number" min="0" step="0.01" placeholder={t('logisticsRequests.quantity')}
                    value={line.quantity} onChange={(e) => updateLine(i, 'quantity', e.target.value)}
                    className="input w-20" />
                  {lines.length > 1 && (
                    <button type="button" onClick={() => setLines((prev) => prev.filter((_, idx) => idx !== i))}
                      className="text-gray-400 hover:text-red-600 text-sm px-2">✕</button>
                  )}
                </div>
              ))}
              <button type="button" onClick={() => setLines((prev) => [...prev, { ...emptyLine }])}
                className="text-sm text-glk-green hover:underline">
                {t('logisticsRequests.addLine')}
              </button>
            </div>

            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => navigate('/requests')} className="btn-ghost flex-1">
                {t('logisticsRequests.cancel')}
              </button>
              <motion.button type="submit" disabled={busy} whileTap={{ scale: 0.98 }} className="btn-primary flex-1">
                {busy ? t('logisticsRequests.sending') : t('logisticsRequests.send')}
              </motion.button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
